import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useId, useState } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { NamePicker } from './NamePicker'
import { StateView } from '@/card/record'
import { formatNumber } from '@/lib/format'
import {
  AdminPage,
  AdminState,
  EditLayer,
  Field,
  LayerButtons,
  Notice,
  PrimaryButton,
  ReadonlyValue,
  SearchBox,
  SkeletonTable,
  Switch,
  isForbidden,
} from '@/admin/AdminShell'
import type { OrganizationOption, PickingStrategy, WarehouseInput, WarehouseRow } from './topologyApi'
import { createWarehouse, fetchWarehouses, pickEmployees, updateWarehouse } from './topologyApi'

export const RESOURCE = 'warehouse.topology'
export const WAREHOUSES_KEY = ['warehouse-topology']

/**
 * Справочник складов (раздел «Склад → Склады и топология»).
 *
 * До этого экрана склады, зоны и ячейки заводились только SQL-скриптами демо. Здесь —
 * список складов со сводкой (зоны, ячейки, занятые, заблокированные) и панель заведения и
 * правки; зоны и ячейки — на странице склада (`/warehouses/:id`).
 *
 * Что держит сервер, а экран только называет: код склада уникален и не меняется (он
 * напечатан в штрихкоде каждой ячейки), организация не меняется (вместе с ней без документа
 * переехал бы остаток). Что вызывающий может, говорит сервер: «Добавить» — по
 * `mayCreateWarehouse` (заводит склады только неограниченное право), «Изменить» у строки — по
 * её `editable` (заведующий правит только склады, где заведующий он, V59). Экран правило не
 * повторяет: проверка прав на клиенте лишь избавляет от кнопки, ведущей к отказу.
 */
export function WarehousesPage() {
  const { t, locale } = useApp()
  const list = useQuery({ queryKey: WAREHOUSES_KEY, queryFn: ({ signal }) => fetchWarehouses(signal) })
  const [draft, setDraft] = useState<WarehouseDraft | null>(null)
  const [query, setQuery] = useState('')
  const forbidden = isForbidden(list.error)

  const all = list.data?.warehouses ?? []
  const mayCreate = list.data?.mayCreateWarehouse ?? false
  const mayEdit = all.some((w) => w.editable)
  const needle = query.trim().toLowerCase()
  const shown = all.filter((w) => !needle || [w.code, w.name, w.address ?? ''].some((v) => v.toLowerCase().includes(needle)))

  return (
    <AdminPage
      sectionKey="nav.section.warehouse"
      titleKey="nav.warehouses"
      count={list.data ? all.length.toLocaleString(locale) : undefined}
      leadKey="topo.lead"
      readOnly={list.isSuccess && !mayEdit && !mayCreate}
      roKey="topo.ro"
      forbidden={forbidden}
      primary={mayCreate && list.data && (
        <PrimaryButton label={t('topo.wh.add')} onClick={() => setDraft(newDraft(list.data.organizations))} />
      )}
    >
      {!forbidden && all.length > 0 && (
        <div className="toolbar">
          <SearchBox value={query} onChange={setQuery} label={t('topo.wh.search')} />
        </div>
      )}
      <AdminState
        isLoading={list.isLoading}
        error={list.error}
        onRetry={() => void list.refetch()}
        forbiddenTitleKey="topo.forbidden"
        errorTitleKey="topo.error"
        skeleton={<SkeletonTable columns={7} rows={5} />}
      >
        {all.length === 0 ? (
          <StateView boxed kind="setup" title={t('topo.wh.setup')} description={t('topo.wh.setup.text')} />
        ) : shown.length === 0 ? (
          <StateView boxed kind="empty" title={t('topo.wh.empty', { query })}
                     actions={<button type="button" className="btn btn-secondary btn-sm" onClick={() => setQuery('')}>{t('adm.resetSearch')}</button>} />
        ) : (
          <div className="tbl-wrap">
            <table className="tbl" aria-label={t('nav.warehouses')}>
              <thead>
                <tr>
                  <th>{t('topo.wh.code')}</th>
                  <th>{t('topo.wh.name')}</th>
                  <th>{t('topo.wh.organization')}</th>
                  <th>{t('topo.wh.strategy')}</th>
                  <th className="num">{t('topo.wh.zones')}</th>
                  <th className="num">{t('topo.wh.cells')}</th>
                  <th>{t('topo.wh.state')}</th>
                  {mayEdit && <th className="adm-act"><span className="sr-only">{t('adm.actions')}</span></th>}
                </tr>
              </thead>
              <tbody>
                {shown.map((w) => (
                  <tr key={w.id}>
                    <td><Link className="mono strong" to={`/warehouses/${w.id}`}>{w.code}</Link></td>
                    <td>
                      <div className="stack" style={{ gap: 2 }}>
                        <span>{w.name}</span>
                        {w.address && <span className="t-xs muted">{w.address}</span>}
                      </div>
                    </td>
                    <td>{w.organizationName ?? '—'}</td>
                    <td>
                      <span className="mono">{w.defaultPickingStrategy}</span>
                      {w.allowsCrossDocking && <span className="badge" style={{ marginLeft: 6 }}>{t('topo.wh.crossDockShort')}</span>}
                    </td>
                    <td className="num">{formatNumber(w.zones, locale)}</td>
                    <td className="num">
                      <span title={t('topo.cells.summary.hint')}>
                        {t('topo.cells.summary', { cells: w.cells, occupied: w.occupiedCells, blocked: w.blockedCells })}
                      </span>
                    </td>
                    <td>
                      <span className={w.active ? 'badge b-success' : 'badge'}>
                        <span className="dot" aria-hidden="true" />
                        {t(w.active ? 'topo.active' : 'topo.inactive')}
                      </span>
                    </td>
                    {mayEdit && (
                      <td className="adm-act">
                        {w.editable ? (
                          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setDraft(editDraft(w))}
                                  aria-label={t('topo.wh.editOf', { code: w.code })}>
                            {t('admin.edit')}
                          </button>
                        ) : (
                          // A foreign site: said in words, so the missing button does not read as a fault.
                          <span className="t-xs muted" title={t('topo.wh.notMine.hint')}>{t('topo.wh.notMine')}</span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminState>
      {draft && list.data && (
        <WarehouseSheet draft={draft} organizations={list.data.organizations} strategies={list.data.strategies}
                        mayAssignManager={list.data.mayAssignManager}
                        onChange={setDraft} onClose={() => setDraft(null)} />
      )}
    </AdminPage>
  )
}

export interface WarehouseDraft {
  id: string | null
  version?: number
  code: string
  name: string
  organizationId: string
  address: string
  manager: { id: string; label: string } | null
  defaultPickingStrategy: PickingStrategy
  allowsCrossDocking: boolean
  active: boolean
}

function newDraft(organizations: OrganizationOption[]): WarehouseDraft {
  return {
    id: null, code: '', name: '', address: '', manager: null,
    // Одна организация — выбирать нечего; несколько — выбор обязан быть сознательным.
    organizationId: organizations.length === 1 ? organizations[0]!.id : '',
    defaultPickingStrategy: 'FIFO', allowsCrossDocking: false, active: true,
  }
}

export function editDraft(w: WarehouseRow): WarehouseDraft {
  return {
    id: w.id, version: w.version, code: w.code, name: w.name, organizationId: w.organizationId,
    address: w.address ?? '',
    manager: w.managerEmployeeId ? { id: w.managerEmployeeId, label: w.managerName ?? w.managerEmployeeId } : null,
    defaultPickingStrategy: w.defaultPickingStrategy, allowsCrossDocking: w.allowsCrossDocking, active: w.active,
  }
}

/** Код печатается в штрихкоде ячейки через точку — точки в нём быть не может. */
export const CODE = /^[A-Za-zА-Яа-яЁё0-9_-]{1,32}$/

export function WarehouseSheet({ draft, organizations, strategies, mayAssignManager, onChange, onClose, onSaved }: {
  draft: WarehouseDraft
  organizations: OrganizationOption[]
  strategies: PickingStrategy[]
  /** Сменить заведующего может только неограниченное право; иначе поле — только чтение. */
  mayAssignManager: boolean
  onChange: (draft: WarehouseDraft) => void
  onClose: () => void
  onSaved?: () => void
}) {
  const { t } = useApp()
  const formId = useId()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const creating = draft.id == null
  const codeError = creating && draft.code !== '' && !CODE.test(draft.code) ? t('topo.codeInvalid') : null
  const incomplete = !CODE.test(draft.code) || draft.name.trim() === '' || draft.organizationId === ''
  const organization = organizations.find((o) => o.id === draft.organizationId)

  const save = useMutation({
    mutationFn: () => {
      const body: WarehouseInput = {
        code: draft.code.trim(),
        name: draft.name.trim(),
        organizationId: draft.organizationId,
        address: draft.address.trim() || null,
        managerEmployeeId: draft.manager?.id ?? null,
        defaultPickingStrategy: draft.defaultPickingStrategy,
        allowsCrossDocking: draft.allowsCrossDocking,
        active: draft.active,
        version: draft.version,
      }
      return draft.id ? updateWarehouse(draft.id, body) : createWarehouse(body)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: WAREHOUSES_KEY })
      onSaved?.()
      onClose()
    },
    onError: (failure) => setError(failure instanceof Error ? failure.message : t('app.error')),
  })

  return (
    <EditLayer
      kind="sheet"
      overline={t('topo.wh.form')}
      title={creating ? t('topo.wh.new') : `${draft.code} — ${draft.name}`}
      onClose={onClose}
      footer={<LayerButtons onCancel={onClose} saveLabel={creating ? t('topo.wh.add') : t('admin.save')}
                            pending={save.isPending} disabled={incomplete} form={formId} />}
    >
      <form id={formId} className="stack" style={{ gap: 14 }} aria-label={t('topo.wh.form')} onSubmit={(event) => {
        event.preventDefault()
        setError(null)
        save.mutate()
      }}>
        <div className="form-grid">
          {creating ? (
            <Field label={t('topo.wh.code')} htmlFor="wh-code" required error={codeError} help={t('topo.code.help')}>
              <input id="wh-code" className={codeError ? 'input mono is-invalid' : 'input mono'} aria-invalid={Boolean(codeError)}
                     maxLength={32} value={draft.code} onChange={(e) => onChange({ ...draft, code: e.target.value.toUpperCase() })} />
            </Field>
          ) : (
            <Field label={t('topo.wh.code')} help={t('topo.code.locked')}>
              <ReadonlyValue mono>{draft.code}</ReadonlyValue>
            </Field>
          )}
          <Field label={t('topo.wh.name')} htmlFor="wh-name" required>
            <input id="wh-name" className="input" maxLength={255} value={draft.name} onChange={(e) => onChange({ ...draft, name: e.target.value })} />
          </Field>
        </div>
        {creating ? (
          <Field label={t('topo.wh.organization')} htmlFor="wh-org" required help={t('topo.wh.organization.help')}>
            <select id="wh-org" className="input" value={draft.organizationId} onChange={(e) => onChange({ ...draft, organizationId: e.target.value })}>
              <option value="">{t('topo.pick')}</option>
              {organizations.map((o) => <option key={o.id} value={o.id}>{o.inn ? `${o.name} · ${o.inn}` : o.name}</option>)}
            </select>
          </Field>
        ) : (
          <Field label={t('topo.wh.organization')} help={t('topo.wh.organization.locked')}>
            <ReadonlyValue>{organization?.name ?? draft.organizationId}</ReadonlyValue>
          </Field>
        )}
        <Field label={t('topo.wh.address')} htmlFor="wh-address">
          <textarea id="wh-address" className="input" rows={2} value={draft.address} onChange={(e) => onChange({ ...draft, address: e.target.value })} />
        </Field>
        {mayAssignManager ? (
          <Field label={t('topo.wh.manager')} htmlFor="wh-manager" help={t('topo.wh.manager.help')}>
            <NamePicker id="wh-manager" kind="employee" search={pickEmployees}
                        value={draft.manager} onChange={(manager) => onChange({ ...draft, manager })} />
          </Field>
        ) : (
          <Field label={t('topo.wh.manager')} help={t('topo.wh.manager.locked')}>
            <ReadonlyValue>{draft.manager?.label ?? '—'}</ReadonlyValue>
          </Field>
        )}
        <Field label={t('topo.wh.strategy')} htmlFor="wh-strategy" help={t('topo.strategy.help')}>
          <select id="wh-strategy" className="input" value={draft.defaultPickingStrategy}
                  onChange={(e) => onChange({ ...draft, defaultPickingStrategy: e.target.value as PickingStrategy })}>
            {strategies.map((s) => <option key={s} value={s}>{t(`topo.strategy.${s}`)}</option>)}
          </select>
        </Field>
        <Field label={t('topo.wh.crossDock')} help={t('topo.wh.crossDock.help')}>
          <Switch checked={draft.allowsCrossDocking} ariaLabel={t('topo.wh.crossDock')}
                  onChange={(value) => onChange({ ...draft, allowsCrossDocking: value })} />
        </Field>
        <Field label={t('topo.wh.active')} help={t('topo.wh.active.help')}>
          <Switch checked={draft.active} ariaLabel={t('topo.wh.active')} onChange={(value) => onChange({ ...draft, active: value })} />
        </Field>
        <Notice text={error} />
      </form>
    </EditLayer>
  )
}
